import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpClientService } from "@/core/services/http/http-methods/http-client.service";
import { GeneralUrlsService } from "@/core/services/http/api-url/general-urls.service";

import { CreateJobResponse } from "@/core/dtos/voice-job.dto";
import { VoiceJobAdapter } from "@/core/adapters/voice-job.adapter";
import {VoiceJob} from "@/core/models/job.model";
import {HttpEvent} from "@angular/common/http";

@Injectable({
  providedIn: 'root'
})
export class VoiceJobService {

  constructor(
      private httpClientService: HttpClientService,
      private endpointUrlsService: GeneralUrlsService,
      private voiceJobAdapter: VoiceJobAdapter
  ) {}


  public initUpload(filename: string): Observable<any> {
    const url = this.endpointUrlsService.getPrivateEndpoint(
        'processingUploadInit'
    );

    return this.httpClientService.post(url, {
      filename
    }).pipe(
        map((data: any) => data)
    );
  }

  public getPartUploadUrl(
      key: string,
      uploadId: string,
      partNumber: number
  ): Observable<any> {
    const url = this.endpointUrlsService.getPrivateEndpoint(
        'processingUploadPartUrl'
    );

    return this.httpClientService.post(url, {
      key,
      upload_id: uploadId,
      part_number: partNumber
    });
  }

  public completeMultipartUpload(
      key: string,
      uploadId: string,
      parts: any[]
  ): Observable<any> {
    const url = this.endpointUrlsService.getPrivateEndpoint(
        'processingUploadComplete'
    );

    return this.httpClientService.post(url, {
      key,
      upload_id: uploadId,
      parts
    });
  }

  public abortMultipartUpload(
      key: string,
      uploadId: string
  ): Observable<any> {
    const url = this.endpointUrlsService.getPrivateEndpoint(
        'processingUploadAbort'
    );

    return this.httpClientService.post(url, {
      key,
      upload_id: uploadId
    });
  }

  public uploadToS3(
      uploadUrl: string,
      file: File
  ): Observable<HttpEvent<any>> {
    return this.httpClientService.put(
        uploadUrl,
        file,
        {
          reportProgress: true,
          observe: 'events',
          skipAuth: true, // important if your interceptor injects JWT
          headers: {
            'Content-Type': file.type || 'application/octet-stream'
          }
        }
    );
  }

  public createJob(
      key: string,
      filename: string,
      finalConversationTitle: string
  ): Observable<any> {
    const url = this.endpointUrlsService.getPrivateEndpoint(
        'processingVoice'
    );

    return this.httpClientService.post(url, {
      key,
      filename,
      final_conversation_title: finalConversationTitle
    }).pipe(
        map((data: any) => data)
    );
  }

  public getJobs(): Observable<VoiceJob[]> {
    const url = this.endpointUrlsService.getPrivateEndpoint('processingVoiceJobs');

    return this.httpClientService.get(url).pipe(
        map((data: any) => this.voiceJobAdapter.adaptList(data))
    );
  }

  public getJob(jobId: string): Observable<VoiceJob> {
    const baseUrl = this.endpointUrlsService.getPrivateEndpoint('processingVoiceJob');

    const url = this.endpointUrlsService.buildUrlPathParameters(baseUrl, {
      jobId: jobId
    });

    return this.httpClientService.get(url).pipe(
        map((data: any) => this.voiceJobAdapter.adapt(data.Job))
    );
  }

  public getDashboard(jobId: string): Observable<any> {
    const baseUrl = this.endpointUrlsService.getPrivateEndpoint(
        'processingVoiceJobDashboard'
    );

    const url = this.endpointUrlsService.buildUrlPathParameters(baseUrl, {
      jobId,
    });

    return this.httpClientService.get(url).pipe(
        map((data: any) => data.dashboard)
    );
  }

  public saveSpeakers(jobId: string, payload: any[]): Observable<any> {
    const baseUrl = this.endpointUrlsService.getPrivateEndpoint('processingVoiceJobSpeakers');

    const url = this.endpointUrlsService.buildUrlPathParameters(baseUrl, {
      jobId: jobId
    });

    return this.httpClientService.post(url, payload ).pipe(
        map((data: any) => data)
    );
  }

  public generateInsights(jobId: string): Observable<any> {
    const baseUrl = this.endpointUrlsService.getPrivateEndpoint(
        'processingVoiceJobInsights'
    );

    const url = this.endpointUrlsService.buildUrlPathParameters(baseUrl, {
      jobId: jobId
    });

    return this.httpClientService.post(url, {}).pipe(
        map((data: any) => data)
    );
  }

  public getInsights(jobId: string): Observable<any> {
    const baseUrl = this.endpointUrlsService.getPrivateEndpoint(
        'processingVoiceJobInsights'
    );

    const url = this.endpointUrlsService.buildUrlPathParameters(baseUrl, {
      jobId,
    });

    return this.httpClientService.get(url).pipe(
        map((data: any) => data.insights)
    );
  }
}